import { Divider, Grid, InputAdornment } from '@mui/material'
import { CatalogueEntry } from 'models/catalogues'
import {
  formatCatalogueLabel,
  renderCatalogueOption,
} from 'lib/helpers/shared.helpers'
import {
  selectCatalogue,
  selectCreatePaymentComplement,
  selectSelectedInvoice,
} from 'lib/helpers/selectors'
import { useAppSelector } from 'store/hooks'
import { IAddPaymentComplementContainerProps } from 'containers/Invoices/PaymentComplements/AddPaymentComplementContainer/AddPaymentComplementContainer'
import { IUseAddPaymentComplementContainer } from 'containers/Invoices/PaymentComplements/AddPaymentComplementContainer/useAddPaymentComplementContainer'
import Button from 'components/Shared/Button/Button'
import IOCustomForm from 'components/FormComponents/IOComponents/IOCustomForm/IOCustomForm'
import AutocompleteField from 'components/FormComponents/IOComponents/AutocompleteField/AutoCompleteField'
import NumericField from 'components/FormComponents/IOComponents/NumericField/NumericField'
import TextField from 'components/FormComponents/IOComponents/TextField/TextField'
import DisplayTotal from 'components/Invoices/DisplayTotal/DisplayTotal'

type TPaymentComplementFormProps = IUseAddPaymentComplementContainer &
  Pick<IAddPaymentComplementContainerProps, 'version'>

const PaymentComplementForm = ({
  addMutation,
  ip,
  isa,
  monto,
  tasaCat,
  tax,
  valid,
  version,
  saveValueToStore,
  setValid,
}: TPaymentComplementFormProps) => {
  const form = useAppSelector(selectCreatePaymentComplement)
  const formasPago = useAppSelector(selectCatalogue('formasPago'))
  const invoice = useAppSelector(selectSelectedInvoice)
  const monedas = useAppSelector(selectCatalogue('monedas'))

  return (
    <IOCustomForm
      onSubmit={async () => addMutation.mutate()}
      onValidity={(v: boolean) => setValid(v)}>
      <div id="section-1" className="content">
        <h3 className="title">(1) Recepción de pagos</h3>
        <Divider className="separator" />
        <p className="subtitle">Datos del pago</p>
        <Divider className="separator" />
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <NumericField
              name="fechaPago"
              label="Fecha de pago"
              value={form?.fechaPago}
              onValue={(v: string) => saveValueToStore('fechaPago', v)}
              validate={['isDateFormatD', 'isValidDateD']}
              format="date"
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <AutocompleteField
              name="formaPago"
              label="Forma de pago"
              catalogue={formasPago}
              value={form?.formaPago}
              onValue={(v: CatalogueEntry) => saveValueToStore('formaPago', v)}
              getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
              getOptionSelected={(
                option: CatalogueEntry,
                value: CatalogueEntry
              ) => option.idCatalogValues === value.idCatalogValues}
              renderOption={(o: any) => renderCatalogueOption(o)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <AutocompleteField
              name="moneda"
              label="Moneda"
              catalogue={monedas}
              value={form?.moneda}
              onValue={(v: CatalogueEntry) => saveValueToStore('moneda', v)}
              getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
              getOptionSelected={(
                option: CatalogueEntry,
                value: CatalogueEntry
              ) => option.idCatalogValues === value.idCatalogValues}
              renderOption={(o: any) => renderCatalogueOption(o)}
              fullWidth
              required
            />
          </Grid>
          {form?.moneda?.value !== 'MXN' && (
            <Grid item xs={4}>
              <NumericField
                name="tipoCambio"
                label="Tipo de cambio"
                value={form?.tipoCambio}
                onValue={(v: string) => saveValueToStore('tipoCambio', v)}
                format="money"
                fullWidth
                required
              />
            </Grid>
          )}
          {version === '3.3' && (
            <Grid item xs={4}>
              <NumericField
                name="monto"
                label="Monto"
                value={form?.monto}
                onValue={(v: string) => saveValueToStore('monto', v)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                format="money"
                fullWidth
                required
              />
            </Grid>
          )}
          <Grid item xs={4}>
            <TextField
              fieldType="number"
              name="numeroOperacion"
              label="Número de operación (opcional)"
              value={form?.numeroOperacion}
              onValue={(v: string) => saveValueToStore('numeroOperacion', v)}
              fullWidth
            />
          </Grid>
        </Grid>
        {version === '4.0' && (
          <>
            <p className="subtitle">Monto del pago</p>
            <Divider className="separator" />
            <Grid container spacing={4}>
              <Grid item xs={4}>
                <NumericField
                  name="monto"
                  label="Monto"
                  value={form?.monto}
                  onValue={(v: string) => saveValueToStore('monto', v)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  format="money"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <AutocompleteField
                  name="tasa"
                  label="Tasa (%)"
                  catalogue={
                    Array.isArray(tasaCat) &&
                    tasaCat.map(v => ({
                      label: `${v * 100}%`,
                      value: v,
                    }))
                  }
                  value={
                    !!form.tasa
                      ? {
                          label: `${Number(form.tasa) * 100}%`,
                          value: form.tasa,
                        }
                      : undefined
                  }
                  onValue={(v: any) =>
                    saveValueToStore('tasa', v ? v.value : undefined)
                  }
                  getOptionLabel={(o: any) => o.label}
                  getOptionSelected={(option: any, value: any) =>
                    option.value === value.value
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <NumericField
                  name="totalWithTax"
                  label="Total"
                  value={monto.add(tax).toString()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    disableUnderline: true,
                  }}
                  inputProps={{ readOnly: true }}
                  format="money"
                  fullWidth
                />
              </Grid>
            </Grid>
          </>
        )}

        <p className="subtitle">Información Adicional</p>
        <Divider className="separator" />
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <TextField
              name="rfcOrigen"
              label="RFC Emisor de cuenta origen (opcional)"
              validate={['isCompanyRFC', 'isPersonRFC']}
              value={form?.rfcOrigen}
              onValue={(v: string) => saveValueToStore('rfcOrigen', v)}
              inputProps={{
                maxLength: 13,
                style: { textTransform: 'uppercase' },
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="rfcBeneficiario"
              label="RFC Emisor cuenta beneficiaria (opcional)"
              validate={['isCompanyRFC', 'isPersonRFC']}
              value={form?.rfcBeneficiario}
              onValue={(v: string) => saveValueToStore('rfcBeneficiario', v)}
              inputProps={{
                maxLength: 13,
                style: { textTransform: 'uppercase' },
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="nombreBanco"
              label="Nombre de banco (opcional)"
              value={form?.nombreBanco}
              onValue={(v: string) => saveValueToStore('nombreBanco', v)}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="cuentaBeneficiario"
              label="Cuenta beneficiario (opcional)"
              value={form?.cuentaBeneficiario}
              onValue={(v: string) => saveValueToStore('cuentaBeneficiario', v)}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="cuentaOrdenante"
              label="Cuenta ordenante (opcional)"
              value={form?.cuentaOrdenante}
              onValue={(v: string) => saveValueToStore('cuentaOrdenante', v)}
              fullWidth
            />
          </Grid>
        </Grid>
        <p className="subtitle" />
        <Divider className="separator" />
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <AutocompleteField
              name="tipoCadenaPago"
              label="Tipo cadena de pago (opcional)"
              catalogue={[]}
              value={form?.tipoCadenaPago}
              onValue={(v: CatalogueEntry) =>
                saveValueToStore('tipoCadenaPago', v)
              }
              getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
              renderOption={(o: any) => renderCatalogueOption(o)}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="certificadoPago"
              label="Certificado de pago (opcional)"
              value={form?.certificadoPago}
              onValue={(v: string) => saveValueToStore('certificadoPago', v)}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="cadenaPago"
              label="Cadena de pago (opcional)"
              value={form?.cadenaPago}
              onValue={(v: string) => saveValueToStore('cadenaPago', v)}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="selloPago"
              label="Sello de pago (opcional)"
              value={form?.selloPago}
              onValue={(v: string) => saveValueToStore('selloPago', v)}
              fullWidth
            />
          </Grid>
        </Grid>
      </div>
      <div id="section-2" className="content">
        <h3 className="title">(2) Documentos relacionados</h3>
        <Divider className="separator" />
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <TextField
              name="idDocumento"
              label="ID del documento"
              value={invoice?.uuid ? invoice.uuid.toUpperCase() : ''}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="serie"
              label="Serie"
              value={invoice?.serie}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="folio"
              label="Folio"
              value={invoice?.folio}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="moneda"
              label="Moneda"
              value={formatCatalogueLabel(invoice?.currency)}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              required
            />
          </Grid>
          {invoice?.currency.value !== 'MXN' && (
            <Grid item xs={4}>
              <NumericField
                name="tipoCambio"
                label="Tipo de cambio"
                value={''}
                InputProps={{ disableUnderline: true }}
                inputProps={{ readOnly: true }}
                format="money"
                fullWidth
                required
              />
            </Grid>
          )}
          <Grid item xs={4}>
            <TextField
              name="metodoPago"
              label="Método de pago"
              value={`${invoice?.paymentMethod?.value} - ${invoice?.paymentMethod?.label}`}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fieldType="number"
              name="numeroParcialidad"
              label="Número de parcialidad"
              validate={['isMoreThanZero']}
              value={form?.numeroParcialidad}
              onValue={(v: string) => saveValueToStore('numeroParcialidad', v)}
              fullWidth
              required={invoice?.paymentMethod?.value === 'PPD'}
            />
          </Grid>
        </Grid>
        <p className="subtitle" />
        <Divider className="separator" />
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <NumericField
              name="importeSaldoAnterior"
              label="Importe saldo anterior"
              defaultValue={isa.toString()}
              value={form?.importeSaldoAnterior}
              onValue={(v: string) =>
                saveValueToStore('importeSaldoAnterior', v)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              format="money"
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <NumericField
              name="importePagado"
              label="Importe pagado"
              defaultValue={ip.toString()}
              value={form?.importePagado}
              onValue={(v: string) => saveValueToStore('importePagado', v)}
              disabled={!form?.importeSaldoAnterior}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              format="money"
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <NumericField
              name="importeInsoluto"
              label="importe insoluto"
              value={isa.subtract(ip).toString()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                disableUnderline: true,
              }}
              inputProps={{ readOnly: true }}
              format="money"
              fullWidth
              required
            />
          </Grid>
        </Grid>
        <div className="invoice-breakdown">
          <DisplayTotal money={form?.moneda?.value} total={monto.toDecimal()} />
        </div>
      </div>
      <Divider className="separator" />
      <div className="actions">
        {process.env.REACT_APP_ENVIRONMENT === 'development' && (
          <Button
            variant="outlined"
            color="secondary"
            activity={addMutation.isLoading}
            disabled={!valid}>
            Vista previa
          </Button>
        )}
        <Button
          id="addPayComplementBtn"
          variant="contained"
          type="submit"
          color="secondary"
          activity={addMutation.isLoading}
          disabled={!valid}>
          Agregar
        </Button>
      </div>
    </IOCustomForm>
  )
}

export default PaymentComplementForm
